import type { ClearPreview } from '../../lib/portalAssets/bulk';
import type { PurgeReason } from '../../lib/portalAssets/purge';
import type { StaffAllParams } from '../../lib/portalAssets/staffAllView';
import { api } from '../../lib/api';
import type {
  PortalAssetCategoryRow,
  PortalAssetCounts,
  PortalAssetListResponse,
  PortalAssetTile,
} from '../../types/portalAsset';
import type { BulkOperation, BulkResponse } from '../../lib/portalAssets/bulk';
import { listQueryString, type PortalAssetsApi, type PortalAssetsBulkApi } from './assetsApi';

// THE STAFF PortalAssetsApi — /api/portal-assets, behind requireAuth.
//
// ⚠️ Never /api/portal/: a staff session at a client endpoint simply 403s, which
// looks like a backend fault rather than the wrong import
// (staffNeverCallsPortalApi.test.ts is the clientPortal twin of this rule;
// staffAssetsApi.test.ts pins it here).

const BASE = '/api/portal-assets';
const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const staffAssetsApi: PortalAssetsApi = {
  cacheKey: 'staff',
  list: (params) => api(`${BASE}${listQueryString(params)}`),
  detail: (assetId) => api(`${BASE}/${encodeURIComponent(assetId)}`),
  patch: (assetId, patch) => api(`${BASE}/${encodeURIComponent(assetId)}`, json('PATCH', patch)),
  remove: (assetId) => api(`${BASE}/${encodeURIComponent(assetId)}`, { method: 'DELETE' }),
  enrich: (assetId) => api(`${BASE}/${encodeURIComponent(assetId)}/enrich`, json('POST', {})),
  review: (assetId, input) => api(`${BASE}/${encodeURIComponent(assetId)}/review`, json('POST', input)),
  history: (assetId) => api(`${BASE}/${encodeURIComponent(assetId)}/history`),
  // The staff counts route is per BRAND; the account tab does not show counts.
  counts: async () => ({ counts: [] }),
  categories: (accountId) => api(`${BASE}/categories/for-account?accountId=${encodeURIComponent(accountId)}`),
  createUploads: (req) => api(`${BASE}/uploads`, json('POST', req)),
  complete: (assetId) => api(`${BASE}/uploads/${encodeURIComponent(assetId)}/complete`, { method: 'POST' }),
};

/**
 * Bulk approve / not-approve / delete / add-to-category / remove-from-category (2026-10-13). ⚠️ Staff only.
 * The response is 200 with a per-asset result even when some fail: read `results`, never the status.
 */
export function bulkPortalAssets(assetIds: readonly string[], operation: BulkOperation): Promise<BulkResponse> {
  return api(`${BASE}/bulk`, json('POST', { assetIds, operation }));
}

/** How much a Clear would destroy — read-only, staff only (Run E). */
export function bulkClearPreview(assetIds: readonly string[]): Promise<{ preview: ClearPreview }> {
  return api(`${BASE}/bulk-clear-preview`, json('POST', { assetIds }));
}

/**
 * Bulk AI enrichment (Run D). ⚠️ Staff only, and ON-DEMAND ONLY: it costs tokens per asset. Same per-asset result
 * shape as bulkPortalAssets — read `results`, never the status.
 */
export function bulkEnrichPortalAssets(assetIds: readonly string[]): Promise<BulkResponse> {
  return api(`${BASE}/bulk-enrich`, json('POST', { assetIds }));
}

/** The staff host's bulk seam (Run F): the bar is shared and names no endpoint, so each host passes its own. */
export const staffBulkApi: PortalAssetsBulkApi = {
  run: bulkPortalAssets,
  enrich: bulkEnrichPortalAssets,
  clearPreview: bulkClearPreview,
};

/** ⚠️ Staff only — there is no client restore route, by decision (2026-10-08). */
export function restorePortalAsset(assetId: string): Promise<PortalAssetTile> {
  return api(`${BASE}/${encodeURIComponent(assetId)}/restore`, { method: 'POST' });
}

/** ⚠️ Staff only, irreversible: removes the files and clears the CDN (purge.ts). */
export function purgePortalAsset(
  assetId: string,
  input: { reason: PurgeReason; note?: string | null },
): Promise<{ assetId: string; objectsRemoved: number; purgedAt: string }> {
  return api(`${BASE}/${encodeURIComponent(assetId)}/purge`, json('POST', input));
}

/**
 * The CROSS-ACCOUNT list: every live asset in the agency, newest first (2026-10-09).
 * ⚠️ Staff only. A brand OR a club, never both (the server refuses the pair).
 */
export function listAllAssets(params: StaffAllParams & { cursor?: string }): Promise<PortalAssetListResponse> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  const qs = q.toString();
  return api(`${BASE}/all${qs ? `?${qs}` : ''}`);
}

export function listBrandAssetCounts(brandAccountId: string): Promise<{ counts: PortalAssetCounts[] }> {
  return api(`${BASE}/counts?brandAccountId=${encodeURIComponent(brandAccountId)}`);
}

export function listAssetCategories(brandAccountId: string): Promise<{ categories: PortalAssetCategoryRow[] }> {
  return api(`${BASE}/categories?brandAccountId=${encodeURIComponent(brandAccountId)}`);
}

/** ⚠️ `kind` is chosen here and NEVER edited afterwards (there is no endpoint that changes it). */
export function createAssetCategory(brandAccountId: string, name: string, kind: string): Promise<unknown> {
  return api(`${BASE}/categories`, json('POST', { brandAccountId, name, kind }));
}

export function editAssetCategory(id: string, patch: { name?: string; description?: string }): Promise<unknown> {
  return api(`${BASE}/categories/${encodeURIComponent(id)}`, json('PATCH', patch));
}

export function reorderAssetCategories(brandAccountId: string, ids: string[]): Promise<unknown> {
  return api(`${BASE}/categories/reorder`, json('POST', { brandAccountId, ids }));
}

export function deleteAssetCategory(id: string): Promise<unknown> {
  return api(`${BASE}/categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
